import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TagComponent } from '@app/shared/components/tag/tag.component';
import { FilterCountBadgeDirective } from '@app/shared/directives/filter-count-badge.directive';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { ProjectTaskUserRole } from '@app/shared/types/enums/project-task-user-role.enum';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { TaskFilters, TaskUserRoleFilter } from '@app/shared/types/models/task/task-filters.model';
import { UiDateRangePickerComponent } from '@app/shared/ui/date-range-picker/ui-date-range-picker.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTreeSelectComponent } from '@app/shared/ui/tree-select/ui-tree-select.component';
import {
  isProffeoUiDateRangeActive,
  ProffeoUiDateRange,
  shouldSyncProffeoUiDateRangeFilter
} from '@app/shared/ui/types/date-picker.types';
import { ProffeoUiSelectOption, proffeoUiSelectSelectionCount } from '@app/shared/ui/types/select.types';
import { parseTaskFilterDate, taskFilterDateTimestamp } from '@app/shared/utils/task-filters.util';
import { TranslatePipe } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { TaskFiltersHelperService } from '../task-filters-helper.service';
import { MOBILE_TASK_GROUPING_OPTIONS, TaskFiltersService } from '../task-filters.service';

/**
 * @summary Advanced task list filters shown in the Filtry floating menu / mobile dialog.
 * @selector proffeo-task-filters-advanced
 */
@Component({
  selector: 'proffeo-task-filters-advanced',
  imports: [
    UiSelectComponent,
    UiTreeSelectComponent,
    UiDateRangePickerComponent,
    TagComponent,
    MatTooltip,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    FilterCountBadgeDirective
  ],
  templateUrl: './task-filters-advanced.component.html'
})
export class TaskFiltersAdvancedComponent {
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly taskFiltersService = inject(TaskFiltersService);
  protected readonly taskFiltersHelperService = inject(TaskFiltersHelperService);
  protected readonly isProffeoUiDateRangeActive = isProffeoUiDateRangeActive;
  protected readonly filters: Signal<TaskFilters | null> = this.taskFiltersService.filters;
  protected readonly today: Signal<Date> = signal(new Date());
  protected readonly priorityOptions: Signal<ProffeoUiSelectOption<TaskPriority>[]> =
    this.taskFiltersService.availablePriorityOptions;
  protected readonly statusOptions: Signal<ProffeoUiSelectOption<TaskStatus>[]> =
    this.taskFiltersService.availableStatusOptions;
  protected readonly industriesOptions: Signal<ProffeoUiSelectOption<string>[]> =
    this.taskFiltersService.availableIndustriesOptions;
  protected readonly usersOptions: Signal<ProffeoUiSelectOption<string>[]> =
    this.taskFiltersService.availableUsersOptions;
  protected readonly taskTypesOptions: Signal<ProffeoUiSelectOption<string>[]> =
    this.taskFiltersService.availableTaskTypes;
  protected readonly tagsOptions: Signal<ProffeoUiSelectOption<string>[]> =
    this.taskFiltersService.availableTagsOptions;
  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly mobileGrouping = this.taskFiltersService.mobileGrouping;
  protected readonly mobileGroupingOptions = MOBILE_TASK_GROUPING_OPTIONS;
  protected readonly myRolesSelection = linkedSignal(() => this.taskFiltersService.selectedMyRoles());
  protected readonly myRolesOptions: ProffeoUiSelectOption<ProjectTaskUserRole>[] = [
    { value: ProjectTaskUserRole.USER, label: 'task-filters.myContractorTasks' },
    { value: ProjectTaskUserRole.INDUSTRY_ADMIN, label: 'task-filters.myIndustryTasks' },
    { value: ProjectTaskUserRole.GROUP_ADMIN, label: 'task-filters.myGroupTasks' }
  ];
  protected readonly myRolesBadgeCount = this.taskFiltersService.myRolesActiveCount;

  protected readonly industrySelection: WritableSignal<string[] | null> = linkedSignal(() => {
    const filters = this.filters();
    if (filters?.noIndustry) {
      return null;
    }
    return filters?.industry ?? [];
  });
  protected readonly userSelection: WritableSignal<string[] | null> = linkedSignal(() => {
    const filters = this.filters();
    if (filters?.noUsers) {
      return null;
    }
    return filters?.user ?? [];
  });
  protected readonly taskStatusSelection: WritableSignal<TaskStatus[]> = linkedSignal(
    () => this.filters()?.taskStatus ?? []
  );
  protected readonly taskPrioritySelection: WritableSignal<TaskPriority[]> = linkedSignal(
    () => this.filters()?.taskPriority ?? []
  );
  protected readonly createdBySelection: WritableSignal<string[]> = linkedSignal(() => this.filters()?.createdBy ?? []);
  protected readonly taskTypeIdsSelection: WritableSignal<string[]> = linkedSignal(
    () => this.filters()?.taskTypeIds ?? []
  );
  protected readonly projectTagIdsSelection: WritableSignal<string[]> = linkedSignal(
    () => this.filters()?.projectTagIds ?? []
  );
  protected readonly locationSelection: WritableSignal<string[] | null> = linkedSignal(() => {
    const filters = this.filters();
    if (filters?.noLocation) {
      return null;
    }
    return filters?.location ?? [];
  });
  protected readonly groupSelection: WritableSignal<string[]> = linkedSignal(() => this.filters()?.group ?? []);

  protected readonly startDateRange: WritableSignal<ProffeoUiDateRange> = linkedSignal(() => ({
    start: parseTaskFilterDate(this.filters()?.startDateFrom),
    end: parseTaskFilterDate(this.filters()?.startDateTo)
  }));
  protected readonly endDateRange: WritableSignal<ProffeoUiDateRange> = linkedSignal(() => ({
    start: parseTaskFilterDate(this.filters()?.endDateFrom),
    end: parseTaskFilterDate(this.filters()?.endDateTo)
  }));
  protected readonly createdDateRange: WritableSignal<ProffeoUiDateRange> = linkedSignal(() => ({
    start: parseTaskFilterDate(this.filters()?.createdDateFrom),
    end: parseTaskFilterDate(this.filters()?.createdDateTo)
  }));
  protected readonly editDateRange: WritableSignal<ProffeoUiDateRange> = linkedSignal(() => ({
    start: parseTaskFilterDate(this.filters()?.editDateFrom),
    end: parseTaskFilterDate(this.filters()?.editDateTo)
  }));

  protected readonly industryBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.industrySelection(), true)
  );
  protected readonly userBadgeCount = computed((): number => proffeoUiSelectSelectionCount(this.userSelection(), true));
  protected readonly taskStatusBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.filters()?.taskStatus)
  );
  protected readonly taskPriorityBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.filters()?.taskPriority)
  );
  protected readonly createdByBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.filters()?.createdBy)
  );
  protected readonly taskTypeBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.filters()?.taskTypeIds)
  );
  protected readonly locationBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.locationSelection(), true)
  );
  protected readonly groupBadgeCount = computed((): number => proffeoUiSelectSelectionCount(this.groupSelection()));
  protected readonly projectTagIdsBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.filters()?.projectTagIds)
  );
  protected readonly userRoleBadgeCount = computed((): number => (this.filters()?.userRole ?? []).length);
  protected readonly userRoleDisplayValue = computed((): string =>
    this.taskFiltersHelperService.userRoleDisplayValue()
  );

  public constructor() {
    effect(() => {
      const roles = this.myRolesSelection();
      if (!isEqual(roles, this.taskFiltersService.selectedMyRoles())) {
        this.taskFiltersService.setMyRolesFilters(roles);
      }
    });
    this.bindEmptyValueFilterSelection('industry', 'noIndustry', this.industrySelection);
    this.bindEmptyValueFilterSelection('location', 'noLocation', this.locationSelection);
    this.bindEmptyValueFilterSelection('user', 'noUsers', this.userSelection);
    this.bindArrayFilterSelection('taskStatus', this.taskStatusSelection);
    this.bindArrayFilterSelection('taskPriority', this.taskPrioritySelection);
    this.bindArrayFilterSelection('createdBy', this.createdBySelection);
    this.bindArrayFilterSelection('taskTypeIds', this.taskTypeIdsSelection);
    this.bindArrayFilterSelection('projectTagIds', this.projectTagIdsSelection);
    this.bindArrayFilterSelection('group', this.groupSelection);
    this.bindDateRangeFilterSelection('startDateFrom', 'startDateTo', this.startDateRange);
    this.bindDateRangeFilterSelection('endDateFrom', 'endDateTo', this.endDateRange);
    this.bindDateRangeFilterSelection('createdDateFrom', 'createdDateTo', this.createdDateRange);
    this.bindDateRangeFilterSelection('editDateFrom', 'editDateTo', this.editDateRange);
  }

  private bindDateRangeFilterSelection(
    fromField: keyof TaskFilters,
    toField: keyof TaskFilters,
    range: WritableSignal<ProffeoUiDateRange>
  ): void {
    effect(() => {
      const next = range();

      if (!shouldSyncProffeoUiDateRangeFilter(next)) {
        return;
      }

      const filters = this.filters();
      const currentStart = (filters?.[fromField] as Date | undefined) ?? null;
      const currentEnd = (filters?.[toField] as Date | undefined) ?? null;
      const nextStartTs = taskFilterDateTimestamp(next.start);
      const nextEndTs = taskFilterDateTimestamp(next.end);
      const currentStartTs = taskFilterDateTimestamp(currentStart);
      const currentEndTs = taskFilterDateTimestamp(currentEnd);

      if (nextStartTs === currentStartTs && nextEndTs === currentEndTs) {
        return;
      }

      this.taskFiltersService.updateDateRangeFilter(fromField, toField, next.start, next.end);
    });
  }

  protected onSelectUserRoleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.taskFiltersHelperService
      .openSelectUserRoleDialog()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: TaskUserRoleFilter[] | undefined) => {
        if (!result) return;
        const activeFilters = result.filter(
          userRoleFilter => userRoleFilter.applicationUserId != null && userRoleFilter.applicationUserId !== ''
        );
        this.taskFiltersService.updateFiltersField('userRole', activeFilters);
      });
  }

  private bindArrayFilterSelection<K extends keyof TaskFilters>(
    fieldName: K,
    selection: WritableSignal<TaskFilters[K]>
  ): void {
    effect(() => {
      const next = selection();
      const current = this.filters()?.[fieldName] ?? [];
      if (!isEqual(next, current)) {
        this.taskFiltersService.updateFiltersField(fieldName, next);
      }
    });
  }

  private bindEmptyValueFilterSelection<K extends keyof TaskFilters>(
    arrayFieldName: K,
    emptyFieldName: keyof TaskFilters,
    selection: WritableSignal<TaskFilters[K] | null>
  ): void {
    effect(() => {
      const value = selection();
      const filters = this.filters();

      if (value === null) {
        const currentEmpty = (filters?.[emptyFieldName] as boolean | undefined) ?? false;
        const currentArray = (filters?.[arrayFieldName] as unknown[] | undefined) ?? [];
        if (!currentEmpty || currentArray.length > 0) {
          this.taskFiltersService.updateFiltersField(emptyFieldName, true);
          this.taskFiltersService.updateFiltersField(arrayFieldName, []);
        }
        return;
      }

      const currentEmpty = (filters?.[emptyFieldName] as boolean | undefined) ?? false;
      const currentArray = (filters?.[arrayFieldName] as unknown[] | undefined) ?? [];
      if (currentEmpty || !isEqual(value, currentArray)) {
        this.taskFiltersService.updateFiltersField(emptyFieldName, false);
        this.taskFiltersService.updateFiltersField(arrayFieldName, value);
      }
    });
  }
}
